import { SkeletonLoader, Typo } from "@pepperstone/pepper-ui-kit";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ProgressSpinner } from "@/components/progress-spinner/ProgressSpinner";
import { useFormatDate } from "@/hooks/useFormatDate";

type LastUpdatedProps = {
  /**
   * timestamp of the last update
   */
  lastUpdated: number;
  /**
   * timestamp of the next update
   */
  nextUpdate: number;
  /**
   * callback to trigger when the update happens
   */
  onUpdate?: () => void;
  /**
   * show skeleton loaders instead of the actual values
   */
  isLoading?: boolean;
  /**
   * Hide text - only display the progress spinner
   */
  hideText?: boolean;
};

/**
 * rendering interval
 */
const UPDATE_INTERVAL = 300;

export const LastUpdatedCounter = ({
  lastUpdated,
  nextUpdate,
  onUpdate,
  isLoading,
  hideText,
}: LastUpdatedProps) => {
  const { t } = useTranslation();
  const { formatDate } = useFormatDate();
  const [progress, setProgress] = useState(0);

  const intervalHandle = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    const onTick = () => {
      if (nextUpdate) {
        const prog =
          ((Date.now() - lastUpdated) / (nextUpdate - lastUpdated)) * 100;
        setProgress(prog);
        if (Date.now() >= nextUpdate) {
          onUpdate?.();
          if (intervalHandle) {
            clearInterval(intervalHandle.current);
          }
        }
      }
    };

    onTick();
    intervalHandle.current = setInterval(() => {
      onTick();
    }, UPDATE_INTERVAL);

    return () => {
      if (intervalHandle.current) {
        clearInterval(intervalHandle.current);
      }
    };
  }, [lastUpdated, nextUpdate, onUpdate]);

  return (
    <div className="ps-flex ps-gap-2 ps-items-center">
      {isLoading ? (
        <>
          {!hideText && (
            <SkeletonLoader width={40} height={5} variant="rectangular" />
          )}
          <ProgressSpinner progress={0} />
        </>
      ) : (
        <>
          {!hideText && (
            <Typo
              size="xs"
              elem="p"
              type="body-v2"
              textColor="ps-text-text-base-secondary"
            >
              {t("common.last_updated", { timestamp: formatDate(lastUpdated) })}
            </Typo>
          )}
          <ProgressSpinner progress={progress} />
        </>
      )}
    </div>
  );
};
